import { supabase } from './supabase'

export type WorkRecord = {
  organisation_id: string
  user_id: string | null
  mailbox_id: string | null
  provider: 'google' | 'microsoft'
  external_thread_id: string
  subject: string | null
  first_opened_at: string | null
  last_activity_at: string
  reply_started_at: string | null
  reply_sent_at: string | null
  active_engagement_seconds: number
  active_draft_seconds: number
  open_count: number
  reply_start_count: number
  reply_send_count: number
  work_status: 'replied' | 'drafting' | 'opened' | 'activity'
  open_to_reply_start_seconds: number | null
  elapsed_reply_window_seconds: number | null

  mailbox_type: 'personal' | 'shared' | null
  handled_by_user_id: string | null
  handled_by_email: string | null
  handled_by_name?: string | null
  attribution_source: string | null
  attribution_confidence: 'high' | 'medium' | 'low' | 'unknown' | null

  inbound_received_at: string | null
  arrival_to_first_open_seconds: number | null
  arrival_to_reply_start_seconds: number | null
  arrival_to_reply_sent_seconds: number | null
  queue_status:
    | 'arrival_unknown'
    | 'not_opened'
    | 'replied'
    | 'drafting'
    | 'opened'

  category_id: string | null
  complexity: 'simple' | 'standard' | 'complex' | 'high_complexity' | null

  benchmark_profile_id: string | null
  queue_target_seconds: number | null
  reply_start_target_seconds: number | null
  reply_sent_target_seconds: number | null
  active_handling_target_seconds: number | null
  active_draft_target_seconds: number | null
  queue_target_met: boolean | null
  reply_start_target_met: boolean | null
  reply_sent_target_met: boolean | null
  active_handling_target_met: boolean | null
  active_draft_target_met: boolean | null
}

export type EmployeeDailyRecord = {
  organisation_id: string
  user_id: string
  mailbox_id: string | null
  provider: 'google' | 'microsoft'
  external_thread_id: string
  activity_day: string
  first_opened_at: string | null
  last_activity_at: string
  reply_started_at: string | null
  reply_sent_at: string | null
  active_engagement_seconds: number
  active_draft_seconds: number
  open_count: number
  reply_start_count: number
  reply_send_count: number
  work_status: 'replied' | 'drafting' | 'opened' | 'activity'
  open_to_reply_start_seconds: number | null
  arrival_to_first_open_seconds: number | null
  inbound_received_at: string | null
  arrival_to_reply_sent_seconds: number | null
  reply_sent_target_seconds: number | null
  reply_sent_target_met: boolean | null
}

export type DailyActivity = {
  organisation_id: string
  activity_day: string
  threads_worked: number
  active_people: number
  threads_replied: number
  threads_in_draft: number
  threads_opened_only: number
  total_active_engagement_seconds: number
  total_active_draft_seconds: number
  average_active_engagement_seconds: number | null
  average_active_draft_seconds: number | null
  average_open_to_reply_start_seconds: number | null
}

export type DailyQueueMetrics = {
  organisation_id: string
  activity_day: string
  threads_worked: number
  threads_with_arrival_context: number
  average_arrival_to_first_open_seconds: number | null
  median_arrival_to_first_open_seconds: number | null
  average_arrival_to_reply_start_seconds: number | null
  average_arrival_to_reply_sent_seconds: number | null
}

export type DailyBenchmarkSummary = {
  organisation_id: string
  activity_day: string
  threads_measured: number
  threads_with_benchmark: number
  queue_target_met_count: number
  queue_target_evaluated_count: number
  reply_start_target_met_count: number
  reply_start_target_evaluated_count: number
  reply_sent_target_met_count: number
  reply_sent_target_evaluated_count: number
}

export type QueuePressure = {
  organisation_id: string
  waiting_threads: number
  waiting_over_15m: number
  waiting_over_30m: number
  waiting_over_60m: number
  average_current_wait_seconds: number
  oldest_current_wait_seconds: number
}

export type DailyOperatingPressure = {
  organisation_id: string
  demand_day: string
  inbound_messages: number
  inbound_threads: number
  outbound_messages: number
  outbound_threads: number
  active_people: number
  threads_worked: number
  threads_replied: number
  inbound_threads_per_active_person: number | null
  reply_output_to_inbound_ratio: number | null
}

export type DailyOperatingSummary = {
  organisation_id: string
  operating_day: string
  inbound_messages: number
  inbound_threads: number
  outbound_messages: number
  outbound_threads: number
  active_people: number
  threads_worked: number
  threads_replied: number
  threads_in_draft: number
  threads_opened_only: number
  total_active_engagement_seconds: number
  total_active_draft_seconds: number
  average_active_engagement_seconds: number | null
  average_active_draft_seconds: number | null
  average_arrival_to_first_open_seconds: number | null
  median_arrival_to_first_open_seconds: number | null
  average_arrival_to_reply_start_seconds: number | null
  average_arrival_to_reply_sent_seconds: number | null
  threads_with_arrival_context: number
  benchmark_threads_measured: number
  threads_with_benchmark: number
  queue_target_met_count: number
  queue_target_evaluated_count: number
  reply_start_target_met_count: number
  reply_start_target_evaluated_count: number
  reply_sent_target_met_count: number
  reply_sent_target_evaluated_count: number
  inbound_threads_per_active_person: number | null
  reply_output_to_inbound_ratio: number | null
  queue_target_met_percent: number | null
  reply_start_target_met_percent: number | null
  reply_sent_target_met_percent: number | null
  replied_vs_inbound_percent: number | null
}

export type HistoryEmployee = {
  userId: string
  displayName: string
  teamName: string
}

export type TeamPerson = {
  userId: string
  displayName: string
  role: string
  teamName: string
  initials: string
  threadsWorked: number
  replied: number
  open: number
  activeSeconds: number
  draftSeconds: number
  averageReplyStartSeconds: number | null
  averageQueueWaitSeconds: number | null
  lastActivityAt: string | null
  status: 'recent' | 'earlier' | 'none'
}

export type HourPoint = {
  label: string
  opened: number
  replied: number
}

function todayStartIso() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

function tomorrowStartIso() {
  const d = new Date()
  d.setHours(24, 0, 0, 0)
  return d.toISOString()
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return parts.slice(0, 2).map(part => part[0]?.toUpperCase() ?? '').join('') || 'MP'
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds == null || !Number.isFinite(seconds)) return '—'
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  const hourLabel = remaining ? `${hours}h ${remaining}m` : `${hours}h`

  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    const dayHours = hours % 24
    const dayLabel = dayHours
      ? `${days}d ${dayHours}h${remaining ? ` ${remaining}m` : ''}`
      : `${days}d${remaining ? ` ${remaining}m` : ''}`

    return `${hourLabel} / ${dayLabel}`
  }

  return hourLabel
}

export function formatRelativeTime(iso: string | null | undefined) {
  if (!iso) return 'No activity yet'
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return 'Just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return new Date(iso).toLocaleDateString()
}

export function shortThreadId(id: string) {
  if (!id) return 'Unknown thread'
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id
}

export function percentMet(met: number, evaluated: number) {
  if (!evaluated) return null
  return Math.round((met / evaluated) * 100)
}

export function formatRatio(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value * 100)}%`
}

async function getOrganisationId() {
  if (!supabase) throw new Error('Supabase is not configured.')

  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError || !authData.user) throw new Error('You are not signed in.')

  const { data, error } = await supabase
    .from('memberships')
    .select('organisation_id')
    .eq('user_id', authData.user.id)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  if (!data?.organisation_id) throw new Error('No organisation membership was found.')

  return { organisationId: data.organisation_id as string, currentUserId: authData.user.id }
}

export async function loadTodayOperations(teamId: string | null = null) {
  if (!supabase) throw new Error('Supabase is not configured.')
  const { organisationId, currentUserId } = await getOrganisationId()
  const start = todayStartIso()
  const end = tomorrowStartIso()

  const teamsResult = await supabase
    .from('teams')
    .select('id,name,start_time,end_time,planned_member_count,sort_order')
    .eq('organisation_id', organisationId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  if (teamsResult.error) throw teamsResult.error

  const teams = (teamsResult.data ?? []) as Array<{ id: string; name: string; start_time: string; end_time: string; planned_member_count: number; sort_order: number }>
  const selectedTeam = teamId ? teams.find(team => team.id === teamId) ?? null : null
  const effectiveTeamId = selectedTeam?.id ?? null
  const teamIds = teams.map(team => team.id)

  let teamMembers: Array<{ team_id: string; user_id: string; display_name: string | null }> = []

  if (teamIds.length) {
    const teamMembersResult = await supabase
      .from('team_members')
      .select('team_id,user_id,display_name')
      .in('team_id', teamIds)

    if (teamMembersResult.error) throw teamMembersResult.error
    teamMembers = teamMembersResult.data ?? []
  }

  const scopedMembers = effectiveTeamId
    ? teamMembers.filter(member => member.team_id === effectiveTeamId)
    : teamMembers

  const mailboxScopeResult = effectiveTeamId
    ? await supabase
        .from('mailbox_team_scope')
        .select('mailbox_id')
        .eq('organisation_id', organisationId)
        .eq('team_id', effectiveTeamId)
    : { data: [], error: null }

  if (mailboxScopeResult.error) {
    console.warn('[Mail Percent] Could not resolve team mailbox scope', mailboxScopeResult.error)
  }
  const mailboxIds = Array.from(new Set((mailboxScopeResult.data ?? []).map(row => String(row.mailbox_id))))

  let recordsResult: { data: unknown[] | null; error: unknown | null }

  if (effectiveTeamId && mailboxIds.length === 0) {
    recordsResult = { data: [], error: null }
  } else {
    let recordsQuery = supabase
      .from('email_work_benchmark_results')
      .select('*')
      .eq('organisation_id', organisationId)
      .gte('last_activity_at', start)
      .lt('last_activity_at', end)

    if (effectiveTeamId) recordsQuery = recordsQuery.in('mailbox_id', mailboxIds)

    recordsResult = await recordsQuery.order('last_activity_at', { ascending: false }) as typeof recordsResult
  }

  if (recordsResult.error) throw recordsResult.error

  const dailySource = effectiveTeamId ? 'team_scope_daily_activity' : 'team_daily_activity'
  const queueMetricsSource = effectiveTeamId ? 'team_scope_daily_queue_metrics' : 'team_daily_queue_metrics'
  const benchmarkSource = effectiveTeamId ? 'team_scope_daily_benchmark_summary' : 'team_daily_benchmark_summary'
  const queuePressureSource = effectiveTeamId ? 'team_scope_queue_pressure' : 'organisation_queue_pressure'
  const operatingPressureSource = effectiveTeamId ? 'team_scope_daily_operating_pressure' : 'daily_operating_pressure'
  const operatingSummarySource = effectiveTeamId ? 'team_scope_daily_operating_summary' : 'daily_operating_summary'

  let dailyQuery = supabase
    .from(dailySource)
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('activity_day', start)
    .lt('activity_day', end)

  let queueMetricsQuery = supabase
    .from(queueMetricsSource)
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('activity_day', start)
    .lt('activity_day', end)

  let benchmarkQuery = supabase
    .from(benchmarkSource)
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('activity_day', start)
    .lt('activity_day', end)

  let queuePressureQuery = supabase
    .from(queuePressureSource)
    .select('*')
    .eq('organisation_id', organisationId)

  let operatingPressureQuery = supabase
    .from(operatingPressureSource)
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('demand_day', start)
    .lt('demand_day', end)

  let operatingSummaryQuery = supabase
    .from(operatingSummarySource)
    .select('*')
    .eq('organisation_id', organisationId)
    .gte('operating_day', start)
    .lt('operating_day', end)

  if (effectiveTeamId) {
    dailyQuery = dailyQuery.eq('team_id', effectiveTeamId)
    queueMetricsQuery = queueMetricsQuery.eq('team_id', effectiveTeamId)
    benchmarkQuery = benchmarkQuery.eq('team_id', effectiveTeamId)
    queuePressureQuery = queuePressureQuery.eq('team_id', effectiveTeamId)
    operatingPressureQuery = operatingPressureQuery.eq('team_id', effectiveTeamId)
    operatingSummaryQuery = operatingSummaryQuery.eq('team_id', effectiveTeamId)
  }

  const [
    dailyResult,
    queueMetricsResult,
    benchmarkSummaryResult,
    queuePressureResult,
    operatingPressureResult,
    operatingSummaryResult
  ] = await Promise.all([
    dailyQuery.maybeSingle(),
    queueMetricsQuery.maybeSingle(),
    benchmarkQuery.maybeSingle(),
    queuePressureQuery.maybeSingle(),
    operatingPressureQuery.maybeSingle(),
    operatingSummaryQuery.maybeSingle()
  ])

  const optionalErrors = [
    [dailySource, dailyResult.error],
    [queueMetricsSource, queueMetricsResult.error],
    [benchmarkSource, benchmarkSummaryResult.error],
    [queuePressureSource, queuePressureResult.error],
    [operatingPressureSource, operatingPressureResult.error],
    [operatingSummarySource, operatingSummaryResult.error],
  ] as const

  for (const [source, queryError] of optionalErrors) {
    if (queryError) {
      console.warn(`[Mail Percent] Optional live-operations source failed: ${source}`, queryError)
    }
  }

  const rawRecords = (recordsResult.data ?? []) as Array<Omit<WorkRecord, 'subject'> & { subject?: string | null }>
  let records: WorkRecord[] = rawRecords.map(record => ({ ...record, subject: record.subject ?? null }))

  if (records.length) {
    const threadIds = Array.from(new Set(records.map(record => record.external_thread_id).filter(Boolean)))
    const subjectMailboxIds = Array.from(new Set(records.map(record => record.mailbox_id).filter((value): value is string => Boolean(value))))

    if (threadIds.length && subjectMailboxIds.length) {
      let subjectQuery = supabase
        .from('provider_message_metadata')
        .select('mailbox_id,provider_thread_id,subject,received_at')
        .eq('organisation_id', organisationId)
        .eq('direction', 'inbound')
        .in('provider_thread_id', threadIds)
        .in('mailbox_id', subjectMailboxIds)
        .not('subject', 'is', null)
        .order('received_at', { ascending: true })
        .limit(5000)

      const subjectResult = await subjectQuery
      if (subjectResult.error) {
        console.warn('[Mail Percent] Could not load email subjects for live work', subjectResult.error)
      } else {
        const subjectByThread = new Map<string, string>()
        for (const row of subjectResult.data ?? []) {
          const subject = typeof row.subject === 'string' ? row.subject.trim() : ''
          if (!subject) continue
          const key = `${row.mailbox_id}:${row.provider_thread_id}`
          if (!subjectByThread.has(key)) subjectByThread.set(key, subject)
        }

        records = records.map(record => ({
          ...record,
          subject: record.mailbox_id
            ? (subjectByThread.get(`${record.mailbox_id}:${record.external_thread_id}`) ?? null)
            : null
        }))
      }
    }
  }

  const daily = (dailyResult.error ? null : dailyResult.data ?? null) as DailyActivity | null
  const queueMetrics = (queueMetricsResult.error ? null : queueMetricsResult.data ?? null) as DailyQueueMetrics | null
  const benchmarkSummary = (benchmarkSummaryResult.error ? null : benchmarkSummaryResult.data ?? null) as DailyBenchmarkSummary | null
  const queuePressure = (queuePressureResult.error ? null : queuePressureResult.data ?? null) as QueuePressure | null
  const operatingPressure = (operatingPressureResult.error ? null : operatingPressureResult.data ?? null) as DailyOperatingPressure | null
  const operatingSummary = (operatingSummaryResult.error ? null : operatingSummaryResult.data ?? null) as DailyOperatingSummary | null
  const teamNameById = new Map(teams.map(team => [team.id, team.name]))
  const recordByUser = new Map<string, WorkRecord[]>()

  for (const record of records) {
    if (!record.user_id) continue
    const list = recordByUser.get(record.user_id) ?? []
    list.push(record)
    recordByUser.set(record.user_id, list)
  }

  const now = Date.now()

  function makePerson(userId: string, displayName: string, teamName: string): TeamPerson {
    const personRecords = recordByUser.get(userId) ?? []
    const replied = personRecords.filter(record => record.work_status === 'replied').length
    const open = personRecords.length - replied
    const activeSeconds = personRecords.reduce((sum, record) => sum + Number(record.active_engagement_seconds || 0), 0)
    const draftSeconds = personRecords.reduce((sum, record) => sum + Number(record.active_draft_seconds || 0), 0)

    const replyStarts = personRecords
      .map(record => record.open_to_reply_start_seconds)
      .filter((value): value is number => value != null && value >= 0)

    const queueWaits = personRecords
      .map(record => record.arrival_to_reply_sent_seconds)
      .filter((value): value is number => value != null && value >= 0)

    const lastActivityAt = personRecords[0]?.last_activity_at ?? null
    const ageMinutes = lastActivityAt ? (now - new Date(lastActivityAt).getTime()) / 60000 : Infinity

    return {
      userId,
      displayName,
      role: 'Team member',
      teamName,
      initials: initials(displayName),
      threadsWorked: personRecords.length,
      replied,
      open,
      activeSeconds,
      draftSeconds,
      averageReplyStartSeconds: replyStarts.length
        ? Math.round(replyStarts.reduce((sum, value) => sum + value, 0) / replyStarts.length)
        : null,
      averageQueueWaitSeconds: queueWaits.length
        ? Math.round(queueWaits.reduce((sum, value) => sum + value, 0) / queueWaits.length)
        : null,
      lastActivityAt,
      status: ageMinutes <= 60 ? 'recent' : ageMinutes <= 1440 ? 'earlier' : 'none'
    }
  }

  const people = scopedMembers.map(member => {
    const displayName = member.display_name?.trim() || (member.user_id === currentUserId ? 'You' : 'Team member')
    return makePerson(member.user_id, displayName, teamNameById.get(member.team_id) ?? 'Team')
  })

  if (!effectiveTeamId) {
    for (const userId of recordByUser.keys()) {
      if (people.some(person => person.userId === userId)) continue
      const displayName = userId === currentUserId ? 'You' : 'Team member'
      people.push(makePerson(userId, displayName, teams[0]?.name ?? 'Team'))
    }
  }

  const hourlyMap = new Map<number, { opened: Set<string>; replied: Set<string> }>()

  for (const record of records) {
    if (record.inbound_received_at) {
      const hour = new Date(record.inbound_received_at).getHours()
      const current = hourlyMap.get(hour) ?? { opened: new Set<string>(), replied: new Set<string>() }
      current.opened.add(record.external_thread_id)
      hourlyMap.set(hour, current)
    }
    if (record.reply_sent_at) {
      const hour = new Date(record.reply_sent_at).getHours()
      const current = hourlyMap.get(hour) ?? { opened: new Set<string>(), replied: new Set<string>() }
      current.replied.add(record.external_thread_id)
      hourlyMap.set(hour, current)
    }
  }

  const hours = Array.from(hourlyMap.keys()).sort((a, b) => a - b)
  const minHour = hours.length ? Math.min(...hours) : Math.max(8, new Date().getHours() - 5)
  const maxHour = hours.length ? Math.max(...hours) : new Date().getHours()

  const hourly: HourPoint[] = []
  for (let hour = minHour; hour <= maxHour; hour++) {
    const point = hourlyMap.get(hour)
    hourly.push({
      label: `${String(hour).padStart(2, '0')}:00`,
      opened: point?.opened.size ?? 0,
      replied: point?.replied.size ?? 0
    })
  }

  const displayNameByUserId = new Map<string, string>()
  for (const member of scopedMembers) {
    const name = member.display_name?.trim()
    if (name) displayNameByUserId.set(member.user_id, name)
  }

  const enrichedRecords = records.map(record => ({
    ...record,
    handled_by_name: record.handled_by_user_id
      ? (displayNameByUserId.get(record.handled_by_user_id) ?? record.handled_by_email ?? 'Team member')
      : (record.handled_by_email ?? null)
  }))

  return {
    organisationId,
    records: enrichedRecords,
    daily,
    queueMetrics,
    benchmarkSummary,
    queuePressure,
    operatingPressure,
    operatingSummary,
    people,
    hourly,
    mailboxIds,
    selectedTeamId: effectiveTeamId,
    team: selectedTeam ?? teams[0] ?? null
  }
}

export async function loadPersonOperations(userId: string, teamId: string | null = null) {
  const data = await loadTodayOperations(teamId)
  const person = data.people.find(item => item.userId === userId) ?? null
  const records = data.records.filter(record => record.user_id === userId)
  return { ...data, person, personRecords: records }
}

export async function loadReportHistory(teamId: string | null = null) {
  if (!supabase) throw new Error('Supabase is not configured.')

  const { organisationId } = await getOrganisationId()
  const source = teamId ? 'team_scope_daily_operating_summary' : 'daily_operating_summary'

  let query = supabase
    .from(source)
    .select('*')
    .eq('organisation_id', organisationId)
    .order('operating_day', { ascending: false })
    .limit(1000)

  if (teamId) query = query.eq('team_id', teamId)

  const { data, error } = await query

  if (error) throw error

  return (data ?? []) as DailyOperatingSummary[]
}

export async function loadEmployeeHistory(userId: string) {
  if (!supabase) throw new Error('Supabase is not configured.')
  const { organisationId } = await getOrganisationId()
  const { data, error } = await supabase
    .from('employee_daily_email_records')
    .select('*')
    .eq('organisation_id', organisationId)
    .eq('user_id', userId)
    .order('activity_day', { ascending: false })
    .order('last_activity_at', { ascending: false })
    .limit(5000)
  if (error) throw error
  return (data ?? []) as EmployeeDailyRecord[]
}

export async function loadOrganisationHistory(teamId: string | null = null) {
  return loadReportHistory(teamId)
}

export async function loadOrganisationEmployeeHistory(teamId: string | null = null) {
  if (!supabase) throw new Error('Supabase is not configured.')

  const { organisationId, currentUserId } = await getOrganisationId()

  let teamsQuery = supabase
    .from('teams')
    .select('id,name')
    .eq('organisation_id', organisationId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })

  if (teamId) teamsQuery = teamsQuery.eq('id', teamId)

  const teamsResult = await teamsQuery
  if (teamsResult.error) throw teamsResult.error

  const teams = (teamsResult.data ?? []) as Array<{ id: string; name: string }>
  const teamNameById = new Map(teams.map(team => [team.id, team.name]))
  const teamIds = teams.map(team => team.id)

  let members: Array<{ team_id: string; user_id: string; display_name: string | null }> = []

  if (teamIds.length) {
    const membersResult = await supabase
      .from('team_members')
      .select('team_id,user_id,display_name')
      .in('team_id', teamIds)

    if (membersResult.error) throw membersResult.error
    members = membersResult.data ?? []
  }

  const memberUserIds = Array.from(new Set(members.map(member => member.user_id)))

  let records: EmployeeDailyRecord[] = []
  if (!teamId || memberUserIds.length > 0) {
    let recordsQuery = supabase
      .from('employee_daily_email_records')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('activity_day', { ascending: false })
      .order('last_activity_at', { ascending: false })
      .limit(10000)

    if (teamId) recordsQuery = recordsQuery.in('user_id', memberUserIds)

    const recordsResult = await recordsQuery
    if (recordsResult.error) throw recordsResult.error
    records = (recordsResult.data ?? []) as EmployeeDailyRecord[]
  }

  const people = new Map<string, HistoryEmployee>()

  for (const member of members) {
    people.set(member.user_id, {
      userId: member.user_id,
      displayName: member.display_name?.trim() || (member.user_id === currentUserId ? 'You' : 'Team member'),
      teamName: teamNameById.get(member.team_id) ?? 'Team'
    })
  }

  for (const record of records) {
    if (!people.has(record.user_id)) {
      people.set(record.user_id, {
        userId: record.user_id,
        displayName: record.user_id === currentUserId ? 'You' : 'Team member',
        teamName: teams[0]?.name ?? 'Team'
      })
    }
  }

  return {
    records,
    people: [...people.values()]
  }
}

export function readableDataError(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message

  if (error && typeof error === 'object') {
    const value = error as { message?: unknown; details?: unknown; hint?: unknown }
    if (typeof value.message === 'string' && value.message.trim()) return value.message
    if (typeof value.details === 'string' && value.details.trim()) return value.details
    if (typeof value.hint === 'string' && value.hint.trim()) return value.hint
  }

  if (typeof error === 'string' && error.trim()) return error
  return fallback
}
